import React, { useState, useContext } from "react";
import { makeStyles } from "@material-ui/styles";
import { AppContext, DispatchContext } from "../AppContext";
import * as actions from "../actions/actions";
import { resolveNotificationMessage, config } from "../helpers/constants";
import Service from "../services/service";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    TextField,
    Typography,
    Box,
} from "@material-ui/core";
import AccountCircleIcon from '@material-ui/icons/AccountCircle';

const useStyles = makeStyles((theme) => ({
    field: {
        width: "100%",
        marginBottom: theme.spacing(2),
    },
}));

// standalone=true: render as a plain form (no Dialog wrapper), used on the /revoke page without login
export default function RevokeOrderForm({ open, onClose, onHide, prefillOrderId, standalone = false }) {
    const state = useContext(AppContext);
    const s = state.strings;
    const dispatch = useContext(DispatchContext);
    const classes = useStyles();

    const isLoggedIn = state.user && state.user.loggedIn;

    const [name, setName] = useState(isLoggedIn ? (state.user.name || "") : "");
    const [customerId, setCustomerId] = useState(isLoggedIn ? String(state.user.userId || "") : "");
    const [email, setEmail] = useState(isLoggedIn ? (state.user.mail || "") : "");
    const [orderId, setOrderId] = useState(String(prefillOrderId || ""));
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [cancelSuccess, setCancelSuccess] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [hidden, setHidden] = useState(false);

    React.useEffect(() => {
        if (open) setHidden(false);
    }, [open]);

    // Sync prefilled values when the dialog opens (only for logged-in dialog mode)
    React.useEffect(() => {
        if (open && isLoggedIn) {
            setName(state.user.name || "");
            setCustomerId(String(state.user.userId || ""));
            setEmail(state.user.mail || "");
            setOrderId(String(prefillOrderId || ""));
        }
        if (!open) setName("");  // reset name on close
    }, [open, prefillOrderId, state.user.name, state.user.userId, state.user.mail]); // eslint-disable-line react-hooks/exhaustive-deps

    const doSubmit = (captchaToken) => {
        setSubmitting(true);
        const userIdParam = parseInt(customerId, 10);
        const tokenParam  = isLoggedIn ? null : captchaToken;
        const emailParam  = isLoggedIn ? null : email.trim();
        const nameParam   = name.trim() || null;
        Service.requestRevocation(orderId, userIdParam, tokenParam, emailParam, nameParam).then((res) => {
            setSubmitting(false);
            if (res.success) {
                const isCancellation = res.message && res.message.indexOf("orderCancelSuccess") >= 0;
                const successMsg = res.message
                    ? resolveNotificationMessage(res.message, s.notificationMessage)
                    : s.notificationMessage.revocationRequestSuccess;
                dispatch(actions.openToast({ type: "success", message: successMsg, status: true }));
                if (isCancellation) {
                    // plain cancellation: just reload order list and close
                    if (!standalone) {
                        Service.getUserInfo().then((userRes) => {
                            if (userRes.success) dispatch(actions.setUserDetails(userRes.data));
                        });
                        onClose();
                    } else {
                        setCancelSuccess(true);
                        setSubmitted(true);
                    }
                } else if (standalone) {
                    // full revocation on standalone page: show success screen
                    setSubmitted(true);
                } else {
                    // full revocation in dialog: close profile, navigate, reload
                    dispatch(actions.setUI({ profileDialogVisible: false }));
                    dispatch(actions.navigate_to("/"));
                    Service.getUserInfo().then((userRes) => {
                        if (userRes.success) dispatch(actions.setUserDetails(userRes.data));
                    });
                    onClose();
                }
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
            }
        });
    };

    const validate = () => {
        const errs = {};
        if (!name.trim())
            errs.name = s.revokeOrderForm.nameInvalid;
        if (!isLoggedIn) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
                errs.email = s.revokeOrderForm.emailInvalid;
        }
        if (!customerId.trim() || !/^\d+$/.test(customerId.trim()))
            errs.customerId = s.revokeOrderForm.customerIdInvalid;
        if (!orderId.trim() || !/^\d+$/.test(orderId.trim()))
            errs.orderId = s.revokeOrderForm.orderIdInvalid;
        return errs;
    };

    const handleSubmit = () => {
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setFieldErrors(errs);
            return;
        }
        setFieldErrors({});

        const confirmAndSubmit = (captchaToken) => {
            dispatch(actions.openConfirmationDialog({
                title: s.revokeOrderForm.confirmTitle,
                text: s.revokeOrderForm.confirmText,
                onConfirm: () => doSubmit(captchaToken),
            }));
        };

        if (!isLoggedIn) {
            // Obtain a fresh captcha token and pass it to the backend for single verification
            window.grecaptcha.ready(() => {
                window.grecaptcha.execute(config.captchaKey, { action: "revoke" }).then((token) => {
                    confirmAndSubmit(token);
                });
            });
        } else {
            confirmAndSubmit(null);
        }
    };

    const formContent = (
        <>
            <Typography paragraph>
                {s.revokeOrderForm.description}
            </Typography>
            <Box mt={2} />
            <TextField
                className={classes.field}
                variant="outlined"
                size="small"
                required
                label={s.revokeOrderForm.name}
                value={name}
                onChange={(e) => { setName(e.target.value); setFieldErrors(prev => ({ ...prev, name: undefined })); }}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || ""}
            />
            <TextField
                className={classes.field}
                variant="outlined"
                size="small"
                label={s.revokeOrderForm.email}
                required
                value={email}
                disabled={isLoggedIn}
                onChange={(e) => { setEmail(e.target.value); setFieldErrors(prev => ({ ...prev, email: undefined })); }}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email || ""}
            />
            <TextField
                className={classes.field}
                variant="outlined"
                size="small"
                label={s.revokeOrderForm.customerId}
                required
                value={customerId}
                disabled={isLoggedIn}
                onChange={(e) => { setCustomerId(e.target.value); setFieldErrors(prev => ({ ...prev, customerId: undefined })); }}
                error={!!fieldErrors.customerId}
                helperText={fieldErrors.customerId || ""}
            />
            <TextField
                className={classes.field}
                variant="outlined"
                size="small"
                label={s.revokeOrderForm.orderId}
                required
                value={orderId}
                onChange={(e) => { setOrderId(e.target.value); setFieldErrors(prev => ({ ...prev, orderId: undefined })); }}
                error={!!fieldErrors.orderId}
                helperText={fieldErrors.orderId || ""}
            />
            <Box mt={1}>
                    <Box component="ul" style={{ margin: "0 0 8px 0", paddingLeft: 20 }}>
                        <Typography component="li" variant="body2" color="secondary" style={{ marginBottom: 4 }}>
                            {s.revokeOrderForm.hintInvoice}
                        </Typography>
                        <Typography component="li" variant="body2" color="secondary">
                            {isLoggedIn ? s.revokeOrderForm.hintSelectContract : s.revokeOrderForm.hintLogin}
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="center">
                    {isLoggedIn ? (
                        <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            startIcon={<AccountCircleIcon />}
                            onClick={() => { setHidden(true); onHide && onHide(); onClose && onClose(); dispatch(actions.navigate_to('/?action=revokeContract')); }}
                            style={{ minWidth: 200, whiteSpace: "nowrap" }}
                        >
                            {s.revokeOrderForm.hintSelectContractLink}
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            startIcon={<AccountCircleIcon />}
                            onClick={() => dispatch(actions.navigate_to('/login?action=revokeContract'))}
                            style={{ minWidth: 200, whiteSpace: "nowrap" }}
                        >
                            {s.revokeOrderForm.hintLoginLink}
                        </Button>
                    )}
                    </Box>
                </Box>
        </>
    );

    const isFormValid = !!name.trim()
        && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
        && /^\d+$/.test(customerId.trim())
        && /^\d+$/.test(orderId.trim());

    const submitButton = (
        <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!isFormValid || submitting}
        >
            {s.revokeOrderForm.submitButton}
        </Button>
    );

    if (hidden) return null;

    if (standalone) {
        if (submitted) {
            return (
                <Box>
                    <Typography variant="h6" paragraph>{s.revokeOrderForm.successTitle || s.revokeOrderForm.title}</Typography>
                    <Typography>{cancelSuccess ? s.notificationMessage.orderCancelSuccess : s.notificationMessage.revocationRequestSuccess}</Typography>
                </Box>
            );
        }
        return (
            <Box>
                {formContent}
                <Box mt={2} display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            if (state.user.loggedIn) {
                                dispatch(actions.navigate_to("/dashboard"));
                            } else {
                                window.location.href = config.backendUrl + s.links.home;
                            }
                        }}
                    >
                        {s.common.cancel}
                    </Button>
                    {submitButton}
                </Box>
            </Box>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{s.revokeOrderForm.title}</DialogTitle>
            <DialogContent dividers>
                {formContent}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" color="primary">
                    {s.common.cancel}
                </Button>
                {submitButton}
            </DialogActions>
        </Dialog>
    );
}
